import React, { useState } from 'react';
import { Form as FinalForm, Field } from 'react-final-form';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import * as validators from '../../../util/validators';
import { NamedLink } from '../../../components';

/**
 * CustomLoginForm: pixel-for-pixel port of the Replit login.tsx card.
 * Accepts the same props as the original LoginForm:
 *   onSubmit, inProgress
 *
 * Renders with Tailwind classes (no CSS modules).
 */
const CustomLoginForm = ({ onSubmit, inProgress }) => {
  const intl = useIntl();
  const [showPassword, setShowPassword] = useState(false);

  const emailRequired = validators.required(
    intl.formatMessage({ id: 'LoginForm.emailRequired' })
  );
  const emailValid = validators.emailFormatValid(
    intl.formatMessage({ id: 'LoginForm.emailInvalid' })
  );
  const passwordRequired = validators.requiredStringNoTrim(
    intl.formatMessage({ id: 'LoginForm.passwordRequired' })
  );

  return (
    <FinalForm
      onSubmit={onSubmit}
      render={({ handleSubmit, invalid, values, errors }) => {
        const submitDisabled = invalid || inProgress;

        const forgotPasswordHref = values?.email && !errors?.email
          ? { search: `email=${encodeURIComponent(values.email)}` }
          : {};

        return (
          <form onSubmit={handleSubmit} className="w-full">
            {/* Email */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1.5" htmlFor="login-email">
                Email Address
              </label>
              <Field
                name="email"
                validate={validators.composeValidators(emailRequired, emailValid)}
              >
                {({ input, meta }) => (
                  <>
                    <input
                      {...input}
                      id="login-email"
                      type="email"
                      autoComplete="email"
                      placeholder="you@example.com"
                      className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    />
                    {meta.touched && meta.error && (
                      <p className="mt-1 text-xs text-destructive">{meta.error}</p>
                    )}
                  </>
                )}
              </Field>
            </div>

            {/* Password */}
            <div className="mb-6">
              <div className="flex items-center justify-between mb-1.5">
                <label className="block text-sm font-medium" htmlFor="login-password">
                  Password
                </label>
                <NamedLink
                  name="PasswordRecoveryPage"
                  to={forgotPasswordHref}
                  className="text-xs text-primary hover:underline"
                >
                  <FormattedMessage id="LoginForm.forgotPassword" />
                </NamedLink>
              </div>
              <Field name="password" validate={passwordRequired}>
                {({ input, meta }) => (
                  <>
                    <input
                      {...input}
                      id="login-password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      placeholder="••••••••"
                      className="w-full h-10 rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                    />
                    {meta.touched && meta.error && (
                      <p className="mt-1 text-xs text-destructive">{meta.error}</p>
                    )}
                  </>
                )}
              </Field>
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={submitDisabled}
              className="w-full h-12 rounded-md bg-primary px-4 py-2 text-sm font-semibold uppercase tracking-wide text-primary-foreground hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
            >
              {inProgress ? (
                <span className="inline-flex items-center gap-2">
                  <span className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
                  Signing in…
                </span>
              ) : (
                'Sign In'
              )}
            </button>

            {/* Footer links */}
            <p className="mt-4 text-center text-xs text-muted-foreground">
              By signing in you agree to our{' '}
              <NamedLink name="TermsOfServicePage" className="underline hover:text-foreground">
                Terms of Service
              </NamedLink>{' '}
              and{' '}
              <NamedLink name="PrivacyPolicyPage" className="underline hover:text-foreground">
                Privacy Policy
              </NamedLink>
              .
            </p>
          </form>
        );
      }}
    />
  );
};

export default CustomLoginForm;
